import { useStore } from '@nanostores/react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { chatId } from '~/lib/persistence/useChatHistory';
import { apiGetMarketing, type MarketingSettingsView } from '~/lib/api/marketing';
import { LoadError, Section, Segmented, SkeletonCard } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { CampaignsView } from './CampaignsView';
import { ConsentView } from './ConsentView';
import { EmailView } from './EmailView';
import { searchConsoleReturn, withoutSearchConsoleParam } from './marketing-model';
import { SearchConsoleView } from './SearchConsoleView';
import { TrackingView } from './TrackingView';
import { WhatsAppView } from './WhatsAppView';

type View = 'tracking' | 'consent' | 'campaigns' | 'search' | 'whatsapp' | 'email';

const VIEWS = [
  ['tracking', 'Tracking'],
  ['consent', 'Consent'],
  ['campaigns', 'Campaigns'],
  ['search', 'Search Console'],
  ['whatsapp', 'WhatsApp'],
  ['email', 'Email'],
] as const;

/**
 * Owner-facing Marketing plugin panel: tracking tags, cookie consent, campaign
 * revenue, Google Search Console, the WhatsApp button and email marketing.
 * The app id IS the chat id. Tracking, Consent and WhatsApp share one settings
 * request; the other sub-views load on their own.
 */
export function MarketingPanel() {
  const appId = useStore(chatId);

  return (
    <div className="relative flex h-full flex-col">
      {appId ? (
        <MarketingPanelBody key={appId} appId={appId} />
      ) : (
        <>
          <PanelHeader />
          <div className="flex flex-1 items-center justify-center px-6 text-center text-[12px] text-muted">
            Start building to set up marketing.
          </div>
        </>
      )}
    </div>
  );
}

function PanelHeader({ children }: { children?: React.ReactNode }) {
  return (
    <div className="border-b border-hairline px-4 py-3.5">
      <div className="flex items-center gap-2.5">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 ring-1 ring-inset ring-accent/15">
          <div className="i-ph:megaphone text-[13px] text-accent" />
        </div>
        <h3 className="text-[13px] font-medium tracking-tight text-primary">Marketing</h3>
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-muted">
        Changes here reach your published site at the next publish. Nothing loads in the preview.
      </p>
      {children}
    </div>
  );
}

function MarketingPanelBody({ appId }: { appId: string }) {
  const [view, setView] = useState<View>(() =>
    typeof window !== 'undefined' && searchConsoleReturn(window.location.search) ? 'search' : 'tracking',
  );
  const settings = useResource(() => apiGetMarketing(appId), appId);

  // Back from Google's sign-in: say how it went once, then drop the marker from the URL.
  useEffect(() => {
    const outcome = searchConsoleReturn(window.location.search);

    if (!outcome) {
      return;
    }

    window.history.replaceState(window.history.state, '', withoutSearchConsoleParam(window.location.href));

    if (outcome === 'connected') {
      toast.success('Google Search Console connected.');
    } else {
      toast.error('Google Search Console was not connected. See the details below.');
    }
  }, []);

  const needsSettings = view === 'tracking' || view === 'consent' || view === 'whatsapp';
  const patchSettings = (patch: Partial<MarketingSettingsView>) =>
    settings.setData((prev) => ({ ...(prev as MarketingSettingsView), ...patch }));

  return (
    <>
      <PanelHeader>
        <div className="mt-3">
          <Segmented label="Marketing section" value={view} options={VIEWS} onChange={setView} />
        </div>
      </PanelHeader>

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {needsSettings &&
          (settings.data ? (
            <>
              {view === 'tracking' && <TrackingView appId={appId} view={settings.data} onChange={settings.setData} />}
              {view === 'consent' && (
                <ConsentView appId={appId} view={settings.data} onSaved={(consent) => patchSettings({ consent })} />
              )}
              {view === 'whatsapp' && (
                <WhatsAppView
                  appId={appId}
                  settings={settings.data.whatsapp}
                  onSaved={(whatsapp) => patchSettings({ whatsapp })}
                />
              )}
            </>
          ) : settings.error ? (
            <LoadError message="Could not load your marketing settings." onRetry={() => void settings.reload()} />
          ) : (
            <Section title="Marketing" icon="i-ph:megaphone">
              <SkeletonCard lines={3} />
              <SkeletonCard />
            </Section>
          ))}

        {view === 'campaigns' && <CampaignsView appId={appId} />}
        {view === 'search' && <SearchConsoleView appId={appId} />}
        {view === 'email' && <EmailView appId={appId} />}
      </div>
    </>
  );
}
